import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Row,
	Col,
	Typography,
	message,
	notification,
	Popconfirm,
	Switch,
	Modal,
	Form,
	Select,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiMedalDuotone,
} from "react-icons/pi";
import {
	requestGetBadgeList,
	requestDeleteBadge,
	requestAwardBadgeToUsers,
} from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title } = Typography;

const RARITY_COLOR = {
	COMMON: "default",
	UNCOMMON: "green",
	RARE: "blue",
	EPIC: "purple",
	LEGENDARY: "gold",
};

const CATEGORY_COLOR = {
	SPECIAL: "volcano",
	ACHIEVEMENT: "cyan",
	EVENT: "magenta",
	DEFAULT: "default",
};

function BadgeListPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [badges, setBadges] = useState([]);
	const [awardModal, setAwardModal] = useState({ open: false, badge: null });
	const [awardLoading, setAwardLoading] = useState(false);
	const [awardForm] = Form.useForm();

	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
	});

	useEffect(() => {
		document.title = "Islamiva Console | Badge Management";
		fetchBadges();
	}, []);

	useEffect(() => {
		fetchBadges();
	}, [pagination.current, pagination.pageSize, filters]);

	useEffect(() => {
		const params = new URLSearchParams();
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());
		if (filters.keyword) params.set("keyword", filters.keyword);
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchBadges = () => {
		setLoading(true);
		requestGetBadgeList({
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: "",
			order: "order:asc",
		})
			.then((response) => {
				const data = response.data.data || [];
				const serverPagination = response.data.pagination || {};
				setBadges(data);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch(() => {
				notification.error({
					message: "Error",
					description: "Failed to fetch badges",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSearch = (value) => {
		setFilters({ ...filters, keyword: value });
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleTableChange = (paginationConfig) => {
		setPagination((prev) => ({
			...prev,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		}));
	};

	const handleDelete = (id) => {
		requestDeleteBadge(id)
			.then(() => {
				message.success("Badge deleted successfully");
				fetchBadges();
			})
			.catch(() => {
				message.error("Failed to delete badge");
			});
	};

	const openAwardModal = (badge) => {
		awardForm.resetFields();
		setAwardModal({ open: true, badge });
	};

	const handleAward = () => {
		awardForm.validateFields().then(({ userIds }) => {
			setAwardLoading(true);
			requestAwardBadgeToUsers(userIds, awardModal.badge._id)
				.then(() => {
					message.success(`Badge awarded to ${userIds.length} user(s)`);
					setAwardModal({ open: false, badge: null });
				})
				.catch((error) => {
					const errMsg =
						error?.response?.data?.message || "Failed to award badge";
					message.error(errMsg);
				})
				.finally(() => setAwardLoading(false));
		});
	};

	const columns = [
		{
			title: "Order",
			dataIndex: "order",
			key: "order",
			width: 70,
			align: "center",
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<Space direction="vertical" size={0}>
					<Typography.Text strong>{text}</Typography.Text>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{record.key}
					</Typography.Text>
				</Space>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			render: (text) => (
				<Typography.Text type="secondary">{text === "-" ? "—" : text}</Typography.Text>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			render: (value) => (
				<Tag color={CATEGORY_COLOR[value] || "default"}>{value}</Tag>
			),
		},
		{
			title: "Rarity",
			dataIndex: "rarity",
			key: "rarity",
			render: (value) => (
				<Tag color={RARITY_COLOR[value] || "default"}>{value}</Tag>
			),
		},
		{
			title: "Active",
			dataIndex: "activated",
			key: "activated",
			align: "center",
			render: (value) => (
				<Switch checked={value} disabled size="small" />
			),
		},
		{
			title: "Actions",
			key: "actions",
			align: "center",
			width: 120,
			render: (_, record) => (
				<Space>
					<Button
						type="text"
						icon={<PiMedalDuotone size={16} />}
						title="Award to users"
						onClick={() => openAwardModal(record)}
					/>
					<Button
						type="text"
						icon={<PiPencilDuotone size={16} />}
						onClick={() => navigate(`/gamification/badges/${record._id}/edit`)}
					/>
					<Popconfirm
						title="Delete Badge"
						description="Are you sure you want to delete this badge?"
						onConfirm={() => handleDelete(record._id)}
						okText="Delete"
						okType="danger"
						cancelText="Cancel">
						<Button
							type="text"
							danger
							icon={<PiTrashDuotone size={16} />}
						/>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<Card
      title={<Title level={4} style={{ margin: 0 }}>Badge Management</Title>}
      extra={
        <Button
          type="primary"
          icon={<PiPlusDuotone />}
          onClick={() => navigate("/gamification/badges/add")}
        >
          Add Badge
        </Button>
      }
    >
			{/* <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
				<Col>
					<Title level={4} style={{ margin: 0 }}>
						Badge Management
					</Title>
				</Col>
				<Col>
					<Button
						type="primary"
						icon={<PiPlusDuotone />}
						onClick={() => navigate("/gamification/badges/add")}>
						Add Badge
					</Button>
				</Col>
			</Row> */}

			<Row style={{ marginBottom: 16 }}>
				<Col flex="auto">
					<SearchInput
						placeholder="Search badges..."
						defaultValue={filters.keyword}
						onSearch={handleSearch}
					/>
				</Col>
			</Row>

			<Table
				rowKey="_id"
				loading={loading}
				dataSource={badges}
				columns={columns}
				pagination={{
					current: pagination.current,
					pageSize: pagination.pageSize,
					total: pagination.total,
					showSizeChanger: true,
					showTotal: (total) => `Total ${total} badges`,
				}}
				onChange={handleTableChange}
			/>

			<Modal
				title={`Award Badge — ${awardModal.badge?.name ?? ""}`}
				open={awardModal.open}
				onOk={handleAward}
				onCancel={() => setAwardModal({ open: false, badge: null })}
				okText="Award"
				confirmLoading={awardLoading}
				destroyOnClose>
				<Form form={awardForm} layout="vertical" style={{ marginTop: 16 }}>
					<Form.Item
						label="User IDs"
						name="userIds"
						rules={[{ required: true, message: "Please enter at least one user ID" }]}
						extra="Type or paste user IDs and press Enter to add each one">
						<Select
							mode="tags"
							placeholder="Paste or type user IDs..."
							tokenSeparators={[",", " ", "\n"]}
							open={false}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
}

export default BadgeListPage;
